// Dashboard.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import SoftBox from 'components/SoftBox';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MiniStatisticsCard from 'examples/Cards/StatisticsCards/MiniStatisticsCard';
import Default1 from './Graph1/Default1';
import CustomdateD from './CustomdateD';
import './Style.css';
import { CircularProgress, Box } from '@mui/material';
import Social from './Graph3/Social';
import Default2 from './Graph2/Default2';
import Default3 from './Graph4/Default3';
import { API_BASE_URL, LAMBDA_BASE_URL } from '../../../constants';
import { useQueryParams } from 'context/QueryParamsContext';
import { useDomain } from 'context/DomainContext';
import { useSites } from 'context/SitesContext';

function Dashboard() {
  const { dateRange, setDateRange } = useQueryParams();
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalVisits, setTotalVisits] = useState(0);
  const [totalSearches, setTotalSearches] = useState(0);
  const [totalLikes, setTotalLikes] = useState(0);
  const [totalDislikes, setTotalDislikes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalEmails, setTotalEmails] = useState(0);
  const [totalCopies, setTotalCopies] = useState(0);
  const [totalContacts, setTotalContacts] = useState(0);
  const [uniqueUsers, setUniqueUsers] = useState(0);
  const [isAuthorized, setIsAuthorized] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const queryString = `?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;

  useEffect(() => {
    const postHeight = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ iframeHeight: height }, '*');
    };
    const observer = new ResizeObserver(postHeight);
    observer.observe(document.body);
    observer.observe(document.documentElement);
    const interval = setInterval(postHeight, 1000);
    setTimeout(postHeight, 100);
    postHeight();
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const domainId = queryParams.get('domain_id');
    if (domainId) {
      sessionStorage.setItem('domainId', domainId);
    } else {
      const storedDomainId = sessionStorage.getItem('domainId');
      const storedSecret = sessionStorage.getItem('secret');
      if (!storedDomainId && !storedSecret) {
        navigate('/Access-Denied');
        return;
      }
    }
    axios
      .get(`${LAMBDA_BASE_URL}/validate-token`, {
        headers: {
          'domain-token': sessionStorage.getItem('domainId'),
        },
      })
      .then((result) => {
        if (result.status === 200) {
          setIsAuthorized(true);
          // console.log('valid token', result);
        }
      })
      .catch(() => {
        setIsAuthorized(false);
        console.log('invalid token');
        navigate('/Access-Denied');
      });
  }, [location.search, navigate]);

  const { domainId } = useDomain();
  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = dateRange;
      const res = await axios.get(`${LAMBDA_BASE_URL}/dashboard_analytics`, {
        params: { start_date, end_date },
        headers: { 'DOMAIN-TOKEN':  sessionStorage.getItem('domainId') || domainId},
      });
      const data = res.data;
      setTotalUsers(data.result.total_users_count);
      setTotalVisits(data.result.total_visit_count);
      setTotalSearches(data.result.total_searches_count);
      setTotalLikes(data.result.total_likes_count);
      setTotalDislikes(data.result.total_dislikes_count);
      setTotalEmails(data.result.total_email_sent_count);
      setTotalCopies(data.result.total_copy_counts);
      setTotalContacts(data.result.total_contactus_count);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { start_date, end_date } = dateRange;
      const res = await axios.get(`${LAMBDA_BASE_URL}/unique-users`, {
        params: { start_date, end_date },
        headers: { 'DOMAIN-TOKEN':  sessionStorage.getItem('domainId') || domainId },
      });
      const data1 = res.data;
      setUniqueUsers(data1.result[0].count);
    } catch (error) {
      console.log('error fetching analytics data:', error);
    }
  };

  useEffect(() => {
    if (dateRange.start_date && dateRange.end_date) {
      fetchAnalytics();
      fetchUsers();
    }
  }, [dateRange,domainId]);

  const handleDateRangeSelect = ({ start, end }) => {
    setDateRange({ start_date: start, end_date: end });
    sessionStorage.setItem('saved_start_date', start);
    sessionStorage.setItem('saved_end_date', end);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('start_date', start);
    queryParams.set('end_date', end);
    navigate({ pathname: location.pathname, search: queryParams.toString() });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" marginLeft="10px" p={0}>
        <CustomdateD onRangeSelect={handleDateRangeSelect} />
      </SoftBox>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px" color="info">
          <CircularProgress color='teal' />
        </Box>
      ) : (
        <SoftBox py={3}>
          <SoftBox mb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/visitors${queryString}`}>
                  <MiniStatisticsCard title={{ text: 'Total Visitors / Visits', fontWeight: 'medium' }} count1={`${totalUsers} / ${totalVisits}`} icon={{ color: 'dark', component: 'groups' }} />
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/queries${queryString}`}>
                  <MiniStatisticsCard title={{ text: 'Total Queries', fontWeight: 'medium' }} count={totalSearches} icon={{ color: 'dark', component: 'search' }} />
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/likes${queryString}`}>
                  <MiniStatisticsCard title={{ text: 'Total Likes', fontWeight: 'medium' }} count={totalLikes} icon={{ color: 'dark', component: 'thumb_up' }} />
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/dislikes${queryString}`}>
                  <MiniStatisticsCard title={{ text: 'Total Dislikes', fontWeight: 'medium' }} count={totalDislikes} icon={{ color: 'dark', component: 'thumb_down' }} />
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/emails-sent${queryString}`}>
                <MiniStatisticsCard title={{ text: 'Emails Sent', fontWeight: 'medium' }} count={totalEmails || 0} icon={{ color: 'dark', component: 'email' }} />
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Link to={`/contact-us${queryString}`}>
                <MiniStatisticsCard title={{ text: 'Contact Us Actions', fontWeight: 'medium' }} count={totalContacts || 0} icon={{ color: 'dark', component: 'perm_contact_calendar_icon' }} />
                </Link>
              </Grid>
            </Grid>
          </SoftBox>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Default1 />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Default2 />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Social dateRange={dateRange} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Default3 />
            </Grid>
          </Grid>
        </SoftBox>
      )}
    </DashboardLayout>
  );
}

export default Dashboard;